import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { adminApi, AdminClient, AdminStaffUser, StaffRole } from '../api/admin';

export default function AdminUsers() {
  const { getToken } = useAuth();
  const [users, setUsers] = useState<AdminStaffUser[] | null>(null);
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    try {
      const t = await getToken();
      const [u, c] = await Promise.all([adminApi.listStaffUsers(t), adminApi.listClients(t)]);
      setUsers(u);
      setClients(c);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // Add-user form state
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<StaffRole>('client_admin');
  const [newClientIds, setNewClientIds] = useState<string[]>([]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await adminApi.createStaffUser(await getToken(), {
        email: newEmail.trim(),
        display_name: newName.trim() || undefined,
        role: newRole,
        client_ids: newRole === 'client_admin' ? newClientIds : [],
      });
      setNewEmail(''); setNewName(''); setNewRole('client_admin'); setNewClientIds([]);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const setUserRole = async (u: AdminStaffUser, role: StaffRole | null) => {
    setBusyId(u.id);
    try {
      await adminApi.updateStaffUser(await getToken(), u.id, { role });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  const setUserClients = async (u: AdminStaffUser, client_ids: string[]) => {
    setBusyId(u.id);
    try {
      await adminApi.updateStaffUser(await getToken(), u.id, { client_ids });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  const setUserDisplayName = async (u: AdminStaffUser, display_name: string) => {
    setBusyId(u.id);
    try {
      await adminApi.updateStaffUser(await getToken(), u.id, { display_name });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  const setUserDisabled = async (u: AdminStaffUser, disabled: boolean) => {
    setBusyId(u.id);
    try {
      await adminApi.updateStaffUser(await getToken(), u.id, { disabled });
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <h1 style={{ color: 'var(--mi-navy)' }}>Users</h1>
      {error && <div style={{ background: '#fee', color: '#900', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      <form onSubmit={add} className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Add staff user</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
          <input placeholder="email@mountinsights.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={input} />
          <input placeholder="Display name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} style={input} />
          <select value={newRole} onChange={(e) => setNewRole(e.target.value as StaffRole)} style={input}>
            <option value="admin">Admin</option>
            <option value="client_admin">Client Admin</option>
          </select>
        </div>
        {newRole === 'client_admin' && (
          <ClientMultiSelect
            clients={clients}
            selected={newClientIds}
            onChange={setNewClientIds}
            placeholder="Assign clients (optional — can set later)"
          />
        )}
        <div style={{ marginTop: 12 }}>
          <button type="submit" className="btn-primary">Add user</button>
        </div>
      </form>

      {!users ? <div>Loading…</div> : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Email</th>
              <th style={th}>Display name</th>
              <th style={th}>Role</th>
              <th style={th}>Assigned clients</th>
              <th style={th}>Status</th>
              <th style={th}>Signed in?</th>
              <th style={th}>Last login</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                clients={clients}
                busy={busyId === u.id}
                onRoleChange={(r) => setUserRole(u, r)}
                onClientsChange={(ids) => setUserClients(u, ids)}
                onDisplayNameChange={(name) => setUserDisplayName(u, name)}
                onDisabledChange={(d) => setUserDisabled(u, d)}
              />
            ))}
            {users.length === 0 && <tr><td style={td} colSpan={8}>No staff users yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

function UserRow({ user, clients, busy, onRoleChange, onClientsChange, onDisplayNameChange, onDisabledChange }: {
  user: AdminStaffUser;
  clients: AdminClient[];
  busy: boolean;
  onRoleChange: (r: StaffRole | null) => void;
  onClientsChange: (ids: string[]) => void;
  onDisplayNameChange: (name: string) => void;
  onDisabledChange: (disabled: boolean) => void;
}) {
  const assignedIds = user.assigned_clients.map((c) => c.client_id);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(user.display_name ?? '');
  const isDisabled = !!user.disabled_at;

  const commitName = () => {
    const next = draftName.trim();
    setEditingName(false);
    if (next && next !== (user.display_name ?? '')) onDisplayNameChange(next);
  };

  return (
    <tr style={{ borderTop: '1px solid var(--mi-border)', opacity: isDisabled ? 0.55 : 1 }}>
      <td style={td}>{user.email}</td>
      <td style={td}>
        {editingName ? (
          <input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setDraftName(user.display_name ?? ''); setEditingName(false); } }}
            autoFocus
            style={{ ...input, width: '100%' }}
          />
        ) : (
          <button
            onClick={() => { setDraftName(user.display_name ?? ''); setEditingName(true); }}
            style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--mi-text)', cursor: 'pointer', textAlign: 'left' }}
            title="Edit display name"
          >
            {user.display_name || <span style={{ color: 'var(--mi-muted)' }}>(none — click to set)</span>}
          </button>
        )}
      </td>
      <td style={td}>
        <select
          value={user.role ?? ''}
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value;
            onRoleChange(v === '' ? null : (v as StaffRole));
          }}
          style={input}
        >
          <option value="admin">Admin</option>
          <option value="client_admin">Client Admin</option>
          <option value="">(none — no access)</option>
        </select>
      </td>
      <td style={td}>
        {user.role === 'client_admin' ? (
          <ClientMultiSelect
            clients={clients}
            selected={assignedIds}
            onChange={onClientsChange}
            compact
          />
        ) : (
          <span style={{ color: 'var(--mi-muted)', fontSize: 13 }}>—</span>
        )}
      </td>
      <td style={td}>
        {isDisabled ? (
          <button onClick={() => onDisabledChange(false)} disabled={busy} style={{ background: 'transparent', border: '1px solid var(--mi-border)', color: 'var(--mi-green)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            Reactivate
          </button>
        ) : (
          <button onClick={() => onDisabledChange(true)} disabled={busy} style={{ background: 'transparent', border: '1px solid var(--mi-border)', color: '#900', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            Mark inactive
          </button>
        )}
      </td>
      <td style={td}>
        {user.staff_sub ? <span style={{ color: 'var(--mi-green)' }}>yes</span> : <span style={{ color: 'var(--mi-muted)' }}>pending</span>}
      </td>
      <td style={td}>
        {user.last_login_at ? (
          <span title={new Date(user.last_login_at).toISOString()}>
            {new Date(user.last_login_at).toLocaleString()}
          </span>
        ) : (
          <span style={{ color: 'var(--mi-muted)' }}>never</span>
        )}
      </td>
      <td style={td}>
        {busy && <span style={{ color: 'var(--mi-muted)', fontSize: 12 }}>saving…</span>}
      </td>
    </tr>
  );
}

function ClientMultiSelect({ clients, selected, onChange, placeholder, compact }: {
  clients: AdminClient[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  compact?: boolean;
}) {
  const chosen = useMemo(() => new Set(selected), [selected]);
  const [q, setQ] = useState('');
  const matches = clients.filter((c) => !chosen.has(c.id) && (q === '' || c.name.toLowerCase().includes(q.toLowerCase())));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', fontSize: 13 }}>
      {selected.map((id) => {
        const c = clients.find((x) => x.id === id);
        if (!c) return null;
        return (
          <span key={id} style={chip}>
            {c.name}
            <button
              onClick={() => onChange(selected.filter((x) => x !== id))}
              style={{ marginLeft: 6, background: 'transparent', border: 'none', color: 'var(--mi-muted)', cursor: 'pointer', padding: 0 }}
            >×</button>
          </span>
        );
      })}
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder ?? (compact ? '+ add…' : 'Add a client…')}
        style={{ ...input, width: compact ? 120 : 220 }}
      />
      {q && matches.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%' }}>
          {matches.slice(0, 10).map((c) => (
            <button
              key={c.id}
              onClick={() => { onChange([...selected, c.id]); setQ(''); }}
              style={{ ...chip, cursor: 'pointer', background: '#eef2ff' }}
            >
              + {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const input: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, overflow: 'hidden' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: 'var(--mi-bg)', fontSize: 13, color: 'var(--mi-muted)', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' };
const chip: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999, background: '#f1f3f5', border: '1px solid var(--mi-border)', fontSize: 12 };
